import { supabasePublic } from "../../config/supabaseClient";
import { isSpaPlatformOrPreviewHost } from "./creatorHost";

/** Bumped so stale apex caches (e.g. browze.co → old username) are dropped. */
const DOMAIN_MAPPING_SLUG_CACHE_PREFIX = "domain_mapping_slug_v2:";
const DOMAIN_MAPPING_SLUG_AT_PREFIX = "domain_mapping_slug_at_v2:";

/** Prefer cache for this long — avoids Slow-4G remaps on every SPA navigation. */
const DOMAIN_MAPPING_TTL_MS = 60 * 60 * 1000; // 1 hour

/** Coalesce parallel Routing + DesignContext lookups for the same host. */
const domainMappingInflight = new Map<string, Promise<string | undefined>>();

/** In-memory hit so pathname changes never wait on localStorage/network. */
const domainMappingSession = new Map<
  string,
  { slug: string; at: number }
>();

function cacheHostsFor(hostname: string): string[] {
  const host = String(hostname || "")
    .trim()
    .toLowerCase();
  if (!host) return [];
  const apex = host.startsWith("www.") ? host.slice(4) : host;
  const www = apex ? `www.${apex}` : "";
  return Array.from(new Set([host, apex, www].filter(Boolean)));
}

function readCachedAt(host: string): number {
  try {
    const raw = localStorage.getItem(`${DOMAIN_MAPPING_SLUG_AT_PREFIX}${host}`);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function readCachedDomainMappingSlug(
  hostname?: string,
): string | undefined {
  try {
    const host = String(
      hostname ??
        (typeof window !== "undefined" ? window.location.hostname : ""),
    )
      .trim()
      .toLowerCase();
    if (!host) return undefined;
    const session = domainMappingSession.get(host);
    if (session?.slug) return session.slug;
    const cached = localStorage.getItem(
      `${DOMAIN_MAPPING_SLUG_CACHE_PREFIX}${host}`,
    );
    const slug = cached ? String(cached).trim().toLowerCase() : "";
    if (!slug) return undefined;
    const at = readCachedAt(host) || Date.now();
    domainMappingSession.set(host, { slug, at });
    return slug;
  } catch {
    return undefined;
  }
}

/** True when local/session cache is fresh enough to skip a network round-trip. */
export function isDomainMappingCacheFresh(hostname?: string): boolean {
  try {
    const host = String(
      hostname ??
        (typeof window !== "undefined" ? window.location.hostname : ""),
    )
      .trim()
      .toLowerCase();
    if (!host) return false;
    const session = domainMappingSession.get(host);
    if (session?.slug && Date.now() - session.at < DOMAIN_MAPPING_TTL_MS) {
      return true;
    }
    const slug = localStorage.getItem(
      `${DOMAIN_MAPPING_SLUG_CACHE_PREFIX}${host}`,
    );
    if (!slug) return false;
    const at = readCachedAt(host);
    return Boolean(at && Date.now() - at < DOMAIN_MAPPING_TTL_MS);
  } catch {
    return false;
  }
}

export function writeCachedDomainMappingSlug(
  hostname: string,
  slug: string,
): void {
  const value = String(slug || "")
    .trim()
    .toLowerCase();
  if (!value) return;
  const at = Date.now();
  try {
    for (const host of cacheHostsFor(hostname)) {
      localStorage.setItem(`${DOMAIN_MAPPING_SLUG_CACHE_PREFIX}${host}`, value);
      localStorage.setItem(`${DOMAIN_MAPPING_SLUG_AT_PREFIX}${host}`, String(at));
      domainMappingSession.set(host, { slug: value, at });
    }
  } catch {
    for (const host of cacheHostsFor(hostname)) {
      domainMappingSession.set(host, { slug: value, at });
    }
  }
}

export function clearCachedDomainMappingSlug(hostname: string): void {
  try {
    for (const host of cacheHostsFor(hostname)) {
      localStorage.removeItem(`${DOMAIN_MAPPING_SLUG_CACHE_PREFIX}${host}`);
      localStorage.removeItem(`${DOMAIN_MAPPING_SLUG_AT_PREFIX}${host}`);
      domainMappingSession.delete(host);
    }
  } catch {
    for (const host of cacheHostsFor(hostname)) {
      domainMappingSession.delete(host);
    }
  }
}

/**
 * Resolve creator slug from `domain_mappings` for custom domains (and www/apex variants).
 * Mirrors the lookup in `Routes/Routing.tsx`.
 *
 * Uses `supabasePublic` (no GoTrue lock) and coalesces in-flight requests so
 * Routing + DesignContext do not double-hit on cold custom-domain loads.
 *
 * Fresh session/localStorage hits skip the network (Slow 4G: every RTT counts).
 * Pass `{ force: true }` to revalidate after a known domain transfer.
 */
export async function fetchCreatorSlugFromDomainMappings(
  hostname?: string,
  options?: { force?: boolean },
): Promise<string | undefined> {
  const host = String(
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : ""),
  )
    .trim()
    .toLowerCase();
  if (!host) return undefined;
  // Never map Vercel / platform SPA hosts to a single creator.
  if (isSpaPlatformOrPreviewHost(host)) return undefined;

  if (!options?.force) {
    const cached = readCachedDomainMappingSlug(host);
    if (cached && isDomainMappingCacheFresh(host)) {
      return cached;
    }
  }

  const apex = host.startsWith("www.") ? host.slice(4) : host;
  const www = apex ? `www.${apex}` : "";
  // Match either apex or www rows (create stores apex; some legacy rows used www).
  const candidates = Array.from(new Set([host, apex, www].filter(Boolean)));
  const inflightKey = candidates.slice().sort().join("|");
  const existing = domainMappingInflight.get(inflightKey);
  if (existing) return existing;

  const task = (async (): Promise<string | undefined> => {
    const baseQuery = () =>
      supabasePublic
        .from("domain_mappings")
        .select("slug")
        .in("domain", candidates)
        .limit(1);

    let data: { slug?: string } | null = null;
    const byStatus = await baseQuery().eq("status", "active").maybeSingle();
    if (byStatus?.error) {
      const byActive = await baseQuery().eq("is_active", true).maybeSingle();
      if (!byActive?.error) data = byActive.data as { slug?: string } | null;
    } else {
      data = byStatus.data as { slug?: string } | null;
    }

    const slugRaw = data?.slug ? String(data.slug) : "";
    const slug = slugRaw.trim().toLowerCase();
    if (slug) writeCachedDomainMappingSlug(host, slug);
    return slug || undefined;
  })();

  domainMappingInflight.set(inflightKey, task);
  try {
    return await task;
  } finally {
    domainMappingInflight.delete(inflightKey);
  }
}
